/**
 * THE PERSONAL BEST — A MAXIMUM, WHICH IS A DIFFERENT KIND OF EARNED THING.
 *
 * CONTRACT.md section 1.5l(a): "Mastering mackerel changes mackerel: larger and
 * rarer variants begin appearing, THE LEDGER RECORDS A PERSONAL BEST, and forms
 * exist that a low-mastery player never sees."
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS: A FENCE THAT PASSED ON A PROMISE.
 * ---------------------------------------------------------------------------
 *
 * Section 6 property 3's dominance probe asserts that no target dominates every
 * other on every axis, and round 11 widened it to an AGGREGATE axis set:
 * throughput, total weight, TOTAL MASTERY, and `recordG` — a per-species best
 * weight. The probe went from red to green on that set and the board recorded
 * the green.
 *
 * `recordG` appeared in NEITHER `src/sim/state.ts` NOR `src/sim/save.ts`. It was
 * computed inside the probe, from the species table, and thrown away when the
 * probe exited. The engine never wrote it, the save never carried it, a reload
 * would have deleted it, and no fence anywhere would have gone red. The fence
 * was measuring an intention.
 *
 * That is a specific failure with a name in this run's doctrine — the apparatus
 * is not the subject (HANDOFF section 3.5). A statistic that is a property of
 * how the measurement was assembled is not a statistic about the game. This file
 * makes the quantity real so that the probe's third axis is a reading off the
 * shipped engine rather than a reading off the probe.
 *
 * ---------------------------------------------------------------------------
 * ONE POINT IS ONE FISH: THE HEAVIEST OF THAT SPECIES, IN GRAMS.
 * ---------------------------------------------------------------------------
 *
 * DERIVED, not chosen. `stepTick` already draws a weight for the quarry —
 * `randRange(seed, tick, CHANNEL.WEIGHT, quarry.minWeightG, quarry.maxWeightG)`
 * — because weight is a COMPOSITION axis the player's choice is allowed to move.
 * The record is that same draw, kept when it beats what is already there. There
 * is no new random surface, no new constant, and nothing new for a paid item to
 * reach: a record can only be set by a cast that has already been paid for in
 * ticks.
 *
 * THREE CONSEQUENCES, each a fact about the engine rather than a preference:
 *
 *  1. IT IS INVARIANT UNDER SKILL, and for a stronger reason than mastery is.
 *     Skill buys cast SPEED, and the weight draw does not read skill at all, so
 *     a skilled player reaches a given record sooner and never reaches a HIGHER
 *     one. The distribution of records over a playthrough is a property of the
 *     species table and the number of casts, and skill moves only the second.
 *
 *  2. IT CANNOT BE RECONSTRUCTED FROM `catches` OR FROM `weightG`. A catch count
 *     says how many; the total weight says how much altogether. Neither records
 *     the largest single draw, and the maximum of n samples is not recoverable
 *     from n and their sum. That is why this is a field and not a derivation, and
 *     it is the same argument mastery.ts makes about cast length.
 *
 *  3. NOTHING ON THE RATE PATH READS IT. `state.records` appears in step.ts
 *     exactly once, on the right of one assignment, for the same forced reason
 *     mastery does: property 2 requires a targeted and an untargeted run to land
 *     the same catch budget with ZERO tolerance, and any rate that read a
 *     per-species record would break that equality on the first tick it applied.
 *
 * ---------------------------------------------------------------------------
 * NEVER-NERF ON A MAXIMUM IS NOT THE SAME RULE AS NEVER-NERF ON A SUM.
 * ---------------------------------------------------------------------------
 *
 * Every other earned quantity in `SimState` rises by `addCapped`, so "it never
 * goes down" is a statement about addition and the only way to break it is a
 * loader that clamps. A record rises by MAXIMUM, and a maximum has a failure
 * mode addition does not have: THE NEXT VALUE IS USUALLY SMALLER. A minnow after
 * a cod is the common case, not the corner case, so an engine that wrote the
 * latest weight instead of the largest would nerf the record several times a
 * minute and every existing fence would stay green — `catches` still rises,
 * `weightG` still rises, the offline invariant still holds exactly, and the two
 * runs being compared are both wrong in the same way.
 *
 * THREE DISTINCT WAYS TO WRITE THAT DEFECT, and record.test.ts fires on each:
 *   - THE SHRINKING CATCH. `records[id] = weight` rather than the maximum.
 *   - THE RELOAD. A loader that rebuilds the map from `catches`, or bounds it by
 *     a gameplay ceiling, or drops ids it does not know.
 *   - THE OFFLINE REPLAY. A settle path that starts from an empty map and merges
 *     back the wrong way, so twenty-four hours away lowers a record that
 *     twenty-four hours present would have raised.
 *
 * All three are the same sentence — a personal best can only rise — and the
 * fence states it once, over the whole run, tick by tick, rather than three
 * times over three code paths.
 */

import { withTallyEntry } from './mastery';

/**
 * THERE IS NO `MAX_RECORD_G`, AND THAT IS A DECISION RATHER THAN AN OMISSION.
 *
 * `mastery` needed `MAX_MASTERY` because it accumulates without limit and could
 * eventually leave safe-integer range. A record cannot: it is ONE DRAW from
 * `randRange(seed, tick, CHANNEL.WEIGHT, quarry.minWeightG, quarry.maxWeightG)`,
 * so it is bounded above by the heaviest species in species.ts — four orders of
 * magnitude below `MAX_WEIGHT_G` — by construction, on every path the engine has.
 * A constant for a bound arithmetic already enforces would be a number nothing
 * reads, which is the same shape as the defect this file exists to close.
 *
 * A LOADED record is a different question and has a different answer: it is
 * EARNED, so `save.ts` bounds it by MAX_EARNED and by nothing narrower, exactly
 * as it does `catches` and `mastery`. See FIELD_CLASS in state.ts.
 */

/**
 * SET A PERSONAL BEST, AND THE WHOLE NEVER-NERF RULE FOR A MAXIMUM IS THE FIRST
 * LINE.
 *
 * A weight that does not beat the standing record returns the map UNCHANGED — the
 * same object, not an equal copy. That is not an optimisation. `stepTick` spreads
 * the state on every landing and `roundtrip.test.ts` proves (load . save) is a
 * fixed point on RAW bytes; a function that rebuilt the map on every no-op landing
 * would reorder nothing today and would be one careless edit away from reordering
 * keys by when a species was last seen, which is SIM-D2 exactly.
 *
 * THE COMPARISON IS `>`, NOT `>=`, and that also matters: an equal weight leaves
 * the record alone, so a record's identity is the FIRST cast that reached it.
 *
 * It delegates to `withTallyEntry` for insertion, so there is exactly one place
 * in this lane that decides what order a per-species map's keys are built in.
 * A second copy of that rule is a second place it can be got wrong — CONTRACT.md
 * section 10 doctrine 11, applied to a function instead of a number.
 */
export function withRecord(
  records: Readonly<Record<string, number>>,
  id: string,
  weightG: number,
): Record<string, number> {
  const standing = records[id] ?? 0;
  // A NON-FINITE OR NEGATIVE WEIGHT CANNOT LOWER A RECORD. The engine cannot
  // produce one, but `openSave` mints earned integers out of `unknown` and a
  // future caller might hand this a value that came from a save. Refusing here
  // costs nothing and makes the guarantee a property of the function rather than
  // a property of its callers.
  if (!Number.isFinite(weightG) || weightG <= standing) return records as Record<string, number>;
  return withTallyEntry(records, id, Math.trunc(weightG));
}

/** The heaviest fish in the save, and which species it was. Null when nothing is landed. */
export function bestOverall(
  records: Readonly<Record<string, number>>,
): { readonly id: string; readonly weightG: number } | null {
  let best: { id: string; weightG: number } | null = null;
  // KEYS IN SORTED ORDER so a tie resolves the same way every run, which is what
  // keeps this readable by a test that compares two states rather than two runs.
  for (const id of Object.keys(records).sort()) {
    const weightG = records[id] ?? 0;
    if (weightG > 0 && (best === null || weightG > best.weightG)) best = { id, weightG };
  }
  return best;
}
